import { Button, Chip, Typography, UserTextRenderer } from "matsuri-ui"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import styled from "@emotion/styled"
import dayjs from "dayjs"
import { css } from "@emotion/react"
import { Link } from "react-router-dom"
import ErrorIcon from "@mui/icons-material/ErrorOutline"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import { useContext } from "react"
import { useListingByCleaningId } from "../../hooks/useListing"
import { useCleaningDetail } from "./providers/cleaningDetail"
import { useCleaningPhotoTourContext } from "./providers/photoTour"
import { KeyboxRotationContext } from "./providers/keyboxrotation"
import { RoomKeyDetails } from "@/templates/RoomKeyDetails"
import { theme } from "@/theme"
import { useCleaning, useCleaningExtraInfo } from "@/hooks/useCleaning"
import { InfoSection } from "@/parts/OperationInfo/InfoSection"
import { ListingOccupancyInfo } from "@/parts/OperationInfo/ListingOccupancyInfo"
import { RoomWifiInfo } from "@/parts/OperationInfo/RoomWifiInfo/RoomWifiInfo"
import { EmbeddedGoogleMap } from "@/components/EmbeddedGoogleMap"
import { RoomGarbageInfo } from "@/parts/OperationInfo/RoomGarbageInfo/RoomGarbageInfo"
import { AddressWithCopy } from "@/parts/OperationInfo/AddressWithCopy"
import { openUrlInNewTab, sumycaListingUrl } from "@/utils/urls"
import { KeyboxRotationCardContainer } from "@/parts/KeyboxRotationCard/KeyboxRotationCardContainer"
import { useRoomKeyInformation } from "@/hooks/useListingBigQueryTable"
import { useTranslation } from "@/hooks/useTranslation"

const informationRootStyle = css`
    display: flex;
    flex-direction: column;
    gap: 40px;
    max-width: 100%;
    padding: 20px;
`

const OtherSiteLink = styled.a`
    && {
        color: ${theme.palette.intentions.primary.dark};
        text-decoration: ${theme.palette.intentions.primary.dark} underline;
    }
`

export const DetailsPage = () => {
    const { t } = useTranslation()
    const { detail } = useCleaningDetail()

    const { token } = useAuth()
    const { data: listing } = useListingByCleaningId(token, detail?.id)
    const { data: cleaningInfoDetailType } = useCleaning(token, detail?.id)
    const { data: cleaningExtraInfo } = useCleaningExtraInfo(token, {
        cleaningId: detail?.id
    })

    const roomKeyInformation = useRoomKeyInformation(token, detail?.id)

    const { tour } = useCleaningPhotoTourContext()

    const { setIsAllRotated } = useContext(KeyboxRotationContext)

    if (detail === undefined) {
        return null
    }

    const { thisReservation, nextReservation } = detail.cleaningInfo

    const listingAddress = detail.cleaningInfo.listingAddress

    const googleMapVariant = listingAddress
        ? ({
              type: "place",
              query: listingAddress
          } as const)
        : undefined

    // お願いごとの一覧
    const cleaningErrands = detail.errands

    return (
        <div>
            <KeyboxRotationCardContainer
                cleaningId={detail.id}
                cleaningDate={detail.cleaningInfo.cleaningDate}
                roomKeyInformation={roomKeyInformation}
                onAllRotated={() => {
                    setIsAllRotated?.(true)
                }}
            />
            {cleaningErrands && cleaningErrands.length > 0 && (
                <div
                    css={css`
                        width: 100%;
                        padding: 16px 20px;
                        background: ${
                            theme.palette.intentions.cleanerBackground.main
                        };
                    `}
                >
                    <Link
                        to={`/cleanings/${detail.id}/errand`}
                        css={css`
                            display: grid;
                            grid-template-columns: auto 1fr auto;
                            gap: 8px;
                            padding: 16px;
                            color: ${theme.palette.text.primary};
                            text-decoration: none;
                            background: white;
                            border-radius: 10px;
                            box-shadow: ${theme.shadows[1]};
                            :hover {
                                background: ${theme.palette.background.default};
                            }
                            :active {
                                background: ${theme.palette.background.default};
                            }
                        `}
                    >
                        <ErrorIcon />
                        <Typography>{t("お願いごとがあります")}</Typography>
                        <ChevronRightIcon />
                    </Link>
                </div>
            )}
            <div
                css={css`
                    width: 100%;
                    padding: 16px 20px;
                `}
            >
                <Typography variant="h4" bolder>
                    {t("物件情報")}
                </Typography>
            </div>
            <div css={informationRootStyle}>
                <InfoSection>
                    {detail.cleaningInfo.photoTourId && (
                        <div>
                            <Chip color="primary">{tour?.title}</Chip>
                        </div>
                    )}
                    <div>
                        <Typography bolder>{t("物件管理名")}</Typography>
                        <Typography>
                            <UserTextRenderer>
                                {detail.cleaningInfo.listingName}
                            </UserTextRenderer>
                        </Typography>
                    </div>
                    <div
                        css={css`
                            display: flex;
                            flex-direction: column;
                            gap: 8px;
                        `}
                    >
                        <Typography bolder>
                            {t("物件の詳細情報（Sumycaリンク）")}
                        </Typography>
                        <Button
                            variant="outlined"
                            color="primary"
                            readOnly={!cleaningExtraInfo?.sumycaListingId}
                            onClick={() => {
                                if (cleaningExtraInfo?.sumycaListingId) {
                                    openUrlInNewTab(
                                        sumycaListingUrl(
                                            cleaningExtraInfo.sumycaListingId
                                        )
                                    )
                                }
                            }}
                            endIcon={<OpenInNewIcon />}
                        >
                            {t("Sumycaで確認する")}
                        </Button>
                        <Typography>{t("※外部リンクで開きます")}</Typography>
                    </div>
                    <div
                        css={css`
                            display: flex;
                            flex-direction: column;
                            gap: 20px;
                        `}
                    >
                        <div>
                            <Typography bolder>{t("物件住所")}</Typography>
                            <AddressWithCopy
                                address={detail.cleaningInfo.listingAddress}
                            />
                        </div>
                        {googleMapVariant && (
                            <EmbeddedGoogleMap variant={googleMapVariant} />
                        )}
                    </div>
                    {detail.cleaningInfo.otaInfo?.length !== 0 && (
                        <div>
                            <Typography bolder>{t("OTA表示名")}</Typography>
                            {detail.cleaningInfo.otaInfo?.map(v => (
                                <div key={v.otaName}>
                                    <Typography variant={"h4"}>
                                        {v.otaName}
                                    </Typography>
                                    <Typography>
                                        <OtherSiteLink
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            href={"https://example.com"}
                                        >
                                            {v.otaListingName}
                                        </OtherSiteLink>
                                    </Typography>
                                </div>
                            ))}
                        </div>
                    )}
                    {detail.cleaningInfo.instruction && (
                        <div>
                            <Typography bolder>
                                {t("チェックインインストラクション")}
                            </Typography>
                            <Typography>
                                <OtherSiteLink
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={detail.cleaningInfo.instruction}
                                >
                                    {t("チェックインインストラクション")}
                                </OtherSiteLink>
                            </Typography>
                        </div>
                    )}
                </InfoSection>

                <ListingOccupancyInfo operationId={detail.id} />

                <InfoSection>
                    <Typography variant="h4" bolder>
                        {t("清掃情報")}
                    </Typography>
                    {detail.scheduleStart && detail.scheduleEnd ? (
                        <div>
                            <Typography bolder>{t("清掃予定")}</Typography>
                            {`${dayjs(detail.cleaningInfo.cleaningDate).format("YYYY-MM-DD")} ${dayjs.unix(detail.scheduleStart).format("HH:mm")}~${dayjs.unix(detail.scheduleEnd).format("HH:mm")}`}
                            <span
                                css={css`
                                    margin-left: 4px;
                                `}
                            >
                                {t(
                                    `(${dayjs
                                        .unix(detail.scheduleEnd)
                                        .diff(
                                            dayjs.unix(detail.scheduleStart),
                                            "minutes"
                                        )}分)`
                                )}
                            </span>
                        </div>
                    ) : null}
                    <div>
                        <Typography bolder>{t("清掃日時")}</Typography>
                        <Typography>
                            {detail.cleaningInfo.cleaningDate}
                        </Typography>
                    </div>
                    {cleaningInfoDetailType?.deadlineAt && (
                        <div>
                            <Typography
                                bolder
                                style={{
                                    color: "red"
                                }}
                            >
                                {t("清掃期限")}
                            </Typography>
                            <Typography
                                style={{
                                    color: "red",
                                    fontWeight: 700
                                }}
                            >
                                {`〜${dayjs.unix(cleaningInfoDetailType.deadlineAt).format("HH:mm")}`}
                            </Typography>
                        </div>
                    )}
                    {detail.cleaningInfo.note && (
                        <div>
                            <Typography bolder>{t("清掃メモ")}</Typography>
                            <Typography>
                                <UserTextRenderer>
                                    {detail.cleaningInfo.note || "-"}
                                </UserTextRenderer>
                            </Typography>
                        </div>
                    )}
                    {thisReservation && (
                        <>
                            <div>
                                <Typography bolder>
                                    {t("今回チェックインのゲスト数")}
                                </Typography>
                                <Typography>
                                    {thisReservation.numberOfGuests}
                                </Typography>
                            </div>
                        </>
                    )}
                    {detail.cleaningCompanyId && (
                        <div>
                            <Typography bolder>
                                {t("担当業者用清掃メモ")}
                            </Typography>
                            <Typography>
                                <UserTextRenderer>
                                    {detail.cleaningCompanyMemo || "-"}
                                </UserTextRenderer>
                            </Typography>
                        </div>
                    )}
                    {nextReservation && (
                        <>
                            <div>
                                <Typography bolder>
                                    {t("次回の予約")}
                                </Typography>
                                <Typography>
                                    {`${nextReservation.staySpan.startDate} ~ ${nextReservation.staySpan.endDate}`}
                                </Typography>
                            </div>
                            <div>
                                <Typography bolder>
                                    {t("次回チェックインのゲスト数")}
                                </Typography>
                                <Typography>
                                    {nextReservation.numberOfGuests}
                                </Typography>
                            </div>
                        </>
                    )}
                    <div>
                        <Typography bolder>{t("物件清掃メモ")}</Typography>
                        <Typography>
                            <UserTextRenderer>
                                {listing?.cleanerNote || "-"}
                            </UserTextRenderer>
                        </Typography>
                    </div>
                    <div>
                        <Typography bolder>{t("清掃種別")}</Typography>
                        <Typography>
                            {cleaningInfoDetailType?.cleaningType !== null &&
                            cleaningInfoDetailType?.cleaningType !== undefined
                                ? ({
                                      standard: t("民泊清掃"),
                                      intensive: t("マンスリー清掃"),
                                      simple: t("簡易清掃"),
                                      deepCleaning: t("ディープクリーニング")
                                  }[cleaningInfoDetailType?.cleaningType] ??
                                  null)
                                : null}
                        </Typography>
                    </div>
                </InfoSection>
                <RoomKeyDetails
                    operationId={detail?.id}
                    listingId={listing?.id}
                    cleaningDateString={detail.cleaningInfo.cleaningDate}
                    roomKeyInformation={roomKeyInformation}
                    // 開始打刻をさせるため、開始前は占有部の鍵情報を開けないようにする
                    isKeyRevealed={detail.status !== "prepared"}
                    guestKeyboxCleaningId={detail?.id}
                />
                <RoomWifiInfo operationId={detail.id} />
                <RoomGarbageInfo operationId={detail.id} />
            </div>
        </div>
    )
}
